function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : layouts distribute parts in a html sekeleton
// =============================================================================
// ---Third-party imports
import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Tab, Loader, Button } from 'semantic-ui-react';
import { ReflexContainer, ReflexSplitter, ReflexElement } from 'uix/chunks';
// ---Framework imports
import connect from 'uix/connect';
import { each, has, get, size, dict, sorted, sum, mapItems, filterItems, flatten } from 'ldash';
import { ucFirst, isSSR } from 'uix/utils';
import log from 'uix/logging';
import { selectRequestClassCfg, getRunId, selectRequestI18N } from 'uix/selectors';
import { Actions } from 'uix/actions';
import { PortalTarget, AutoSizer, ResizableDialog } from 'uix/components';
import { useResizeObserver, useCallback } from 'uix/hooks';
import { createParts } from 'uix/parts';
import { translate } from 'uix/i18n';
// ---Local imports
import './layouts.global.css';
import { persistSplitter } from './actions';
// ---Globals
class Layout extends Component {
    render() {
        return /*#__PURE__*/ _jsx("div", {
            className: this.cssClassName(),
            children: "LAYOUT"
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "name", ()=>'Layout'), _define_property(this, "cssClassName", ()=>{
            let { classname, tabname, theme = 'default' } = this.props;
            let c = 'class' + ucFirst(classname || '');
            let t = 'tab' + ucFirst(tabname || '');
            return `layout ${this.name()} ${c} ${t} theme ${theme}`;
        });
    }
}
_define_property(Layout, "propTypes", {
    parts: PropTypes.shape({
        classlevel: PropTypes.array.isRequired,
        tablevel: PropTypes.array.isRequired
    }).isRequired
});
const orderSorted = (parts)=>sorted(parts, ([part, cfg])=>parseInt(cfg.order || 0));
class Blank extends Layout {
    render() {
        let { classlevel, tablevel } = this.props.parts;
        let parts = [
            ...classlevel,
            ...tablevel
        ];
        return /*#__PURE__*/ _jsx("div", {
            className: this.cssClassName(),
            children: createParts(orderSorted(parts))
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "name", ()=>'Blank');
    }
}
class L1 extends Blank {
    constructor(...args){
        super(...args), /* future frontend layout?
   */ _define_property(this, "name", ()=>'L1');
    }
}
class L1Col extends Blank {
    constructor(...args){
        super(...args), /* future frontend layout?
   */ _define_property(this, "name", ()=>'L1Col');
    }
}
class L111 extends Layout {
    alignParts() {
        /* create parts and group by align attribute
     */ let parts = {};
        each([
            'top',
            'left',
            'center',
            'right',
            'bottom',
            'dialog',
            'centerTop'
        ], (align)=>{
            let { classlevel, tablevel } = this.props.parts;
            let filtered = [
                ...classlevel,
                ...tablevel
            ].filter(([part, cfg])=>cfg.align == align || !cfg.align && align == 'center');
            parts[align] = createParts(orderSorted(filtered));
        });
        return parts;
    }
    containerElement(parts, cprops) {
        return /*#__PURE__*/ _jsx("div", {
            ...cprops,
            children: parts
        });
    }
    container(parts, cprops) {
        if (!size(parts)) return null;
        return this.containerElement(parts, cprops);
    }
    dialogContainer(parts) {
        if (!size(parts)) return null;
        return /*#__PURE__*/ _jsx(ResizableDialog, {
            overflow: "hidden",
            closeButton: false,
            name: "LayoutDialogParts",
            storeKey: "Global/LayoutDialogParts",
            children: parts
        });
    }
    render() {
        let { top, left, center, right, bottom, dialog } = this.alignParts();
        let centerParts = flatten([
            left,
            center,
            right
        ].map((e)=>e || []));
        return /*#__PURE__*/ _jsxs("div", {
            id: "flexWrapper",
            className: this.cssClassName(),
            children: [
                this.container(top, {
                    id: 'header'
                }),
                this.container(centerParts, {
                    id: 'center'
                }),
                this.container(bottom, {
                    id: 'footer'
                }),
                this.dialogContainer(dialog)
            ]
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "name", ()=>'L111');
    }
}
class L131 extends L111 {
    componentDidMount() {
        if (!isSSR) {
            document.documentElement.style.overflow = 'hidden';
        }
    }
    render() {
        let parts = this.alignParts();
        return /*#__PURE__*/ _jsxs("div", {
            id: "flexWrapper",
            className: this.cssClassName(),
            children: [
                this.container(parts.top, {
                    id: 'header'
                }),
                /*#__PURE__*/ _jsxs("div", {
                    id: "center",
                    children: [
                        this.container(parts.left, {
                            id: 'centerLeft'
                        }),
                        this.container(parts.center, {
                            id: 'centerMiddle'
                        }),
                        this.container(parts.right, {
                            id: 'centerRight'
                        })
                    ]
                }),
                this.container(parts.bottom, {
                    id: 'footer'
                }),
                this.dialogContainer(parts.dialog)
            ]
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "name", ()=>'L131');
    }
}
const RESPOND_WIDTH = 600;
const Responsive = ({ parts })=>{
    let [responded, setResponded] = useState(false);
    let onResize = useCallback(({ width })=>{
        let needReponse = width < RESPOND_WIDTH;
        if (responded !== needReponse) {
            setResponded(needReponse);
        }
    }, [
        responded
    ]);
    let ref = useResizeObserver({
        onResize
    });
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        children: responded ? /*#__PURE__*/ _jsx(L111, {
            parts: parts
        }) : /*#__PURE__*/ _jsx(L131, {
            parts: parts
        })
    });
};
const TogglePaneButton = ({ onClick })=>{
    let [hovered, setHovered] = useState(false);
    let s = {
        cursor: 'pointer',
        backgroundColor: hovered ? '#DDD' : '#EEE',
        position: 'absolute',
        textAlign: 'center',
        fontSize: '0.7em'
    };
    if (hovered) {
        s = {
            ...s,
            width: '20px',
            marginLeft: '-10px'
        };
    }
    return /*#__PURE__*/ _jsx("div", {
        style: s,
        onClick: onClick,
        onMouseEnter: ()=>setHovered(true),
        onMouseLeave: ()=>setHovered(false),
        children: !hovered ? /*#__PURE__*/ _jsx(_Fragment, {
            children: " "
        }) : /*#__PURE__*/ _jsx(_Fragment, {
            children: "✖"
        })
    });
};
class L131Split extends L131 {
    shouldComponentUpdate(nextProps, nextState) {
        // no update if only splitter props received
        if (this.needsSplitterUpdate) {
            this.needsSplitterUpdate = false;
            return true;
        }
        return this.props.parts !== nextProps.parts;
    }
    containerElement(parts, cprops) {
        // build contCenter, contleft, contRight
        cprops = {
            ...cprops,
            className: `cont${ucFirst(cprops.name || cprops.id || '')}`
        };
        if (has(cprops, 'id')) {
            return /*#__PURE__*/ _jsx("div", {
                ...cprops,
                children: parts
            });
        } else {
            return /*#__PURE__*/ _jsx(ReflexElement, {
                ...cprops,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "flexSplitterContainer",
                    children: parts
                })
            }, cprops.name);
        }
    }
    centerContainer(parts, cprops) {
        // if (!size(parts.center || parts.centerTop)) {
        //   return null
        // }
        cprops = {
            ...cprops,
            className: `cont${ucFirst(cprops.name || cprops.id || '')}`
        };
        let toptabs = null;
        if (parts.center[0]?.key.startsWith('TreeTopTabs')) {
            toptabs = parts.center[0];
            parts.center = parts.center.slice(1);
        }
        let bottomtabs = null;
        if (parts.center.slice(-1)[0]?.key.startsWith('MajorTabGroup')) {
            bottomtabs = parts.center.slice(-1)[0];
            parts.center = parts.center.slice(0, -1);
        }
        return /*#__PURE__*/ _jsx(ReflexElement, {
            ...cprops,
            children: /*#__PURE__*/ _jsx("div", {
                className: "flexSplitterContainer",
                children: /*#__PURE__*/ _jsxs("div", {
                    style: {
                        display: 'flex',
                        height: '100%',
                        width: '100%'
                    },
                    children: [
                        /*#__PURE__*/ _jsx(PortalTarget, {
                            id: "centerLeftPortal"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "centerFlexWrapper",
                            children: [
                                this.container(parts.centerTop, {
                                    name: 'header'
                                }),
                                /*#__PURE__*/ _jsx(PortalTarget, {
                                    id: "topPortal"
                                }),
                                toptabs,
                                /*#__PURE__*/ _jsx("div", {
                                    className: "centerFlexContainer",
                                    children: parts.center
                                }),
                                /*#__PURE__*/ _jsx(PortalTarget, {
                                    id: "centerBottomPortal"
                                }),
                                bottomtabs
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(PortalTarget, {
                                id: "centerRightPortal"
                            })
                        })
                    ]
                })
            })
        });
    }
    render() {
        let parts = this.alignParts();
        let { left, right } = this.splitters;
        let center = 1;
        if (parts.left) center -= left;
        if (parts.right) center -= right;
        return /*#__PURE__*/ _jsxs("div", {
            id: "flexWrapper",
            className: this.cssClassName(),
            children: [
                this.dialogContainer(parts.dialog, {
                    name: 'dialog'
                }),
                /*#__PURE__*/ _jsxs(ReflexContainer, {
                    orientation: "vertical",
                    children: [
                        /*#__PURE__*/ _jsx(_Fragment, {
                            children: /*#__PURE__*/ _jsx(PortalTarget, {
                                id: "leftPortal"
                            })
                        }),
                        this.container(parts.left, {
                            name: 'left',
                            flex: left,
                            onResize: this.onResizePane
                        }),
                        parts.left && /*#__PURE__*/ _jsx(ReflexSplitter, {
                            propagate: true,
                            className: "layoutSplitter",
                            children: /*#__PURE__*/ _jsx(TogglePaneButton, {
                                onClick: ()=>this.togglePane('left')
                            })
                        }),
                        this.centerContainer(parts, {
                            name: 'center',
                            flex: center,
                            minSize: 60
                        }),
                        parts.center && parts.right && /*#__PURE__*/ _jsx(ReflexSplitter, {
                            propagate: true,
                            className: "layoutSplitter"
                        }),
                        this.container(parts.right, {
                            name: 'right',
                            flex: right,
                            onResize: this.onResizePane
                        })
                    ]
                }),
                this.container(parts.bottom, {
                    name: 'footer'
                })
            ]
        });
    }
    constructor(props){
        super(props), _define_property(this, "name", ()=>'L131Split'), _define_property(this, "onResizePane", (event)=>{
            let { persistSplitter } = this.props;
            let { name, flex } = event.component.props;
            // fast internal update
            this.splitters[name] = flex;
            // persist throttled
            //~ log('send %o %o', name, flex)
            persistSplitter('L131Split', name, flex);
        }), _define_property(this, "togglePane", (name)=>{
            let { persistSplitter, ui } = this.props;
            let nextValue = 0;
            if (!ui[name] || ui[name] < 0.05) {
                nextValue = 0.18;
            }
            this.needsSplitterUpdate = true; // this is so ugly, but we want to rerender if external left/right props are updated
            this.splitters[name] = nextValue;
            persistSplitter('L131Split', name, nextValue); // this will triger a rerender
        });
        this.splitters = {
            ...this.props.ui
        };
    }
}
/* 131 Layout with splitters
  see: https://leefsmp.github.io/Re-Flex/index.html
  */ _define_property(L131Split, "uiDefaults", {
    left: 0.2,
    right: 0.2,
    __version__: 3,
    __name__: 'L131Split'
});
_define_property(L131Split, "storeMap", (root)=>({
        classname: root.meta.request.classname,
        tabname: root.meta.request.tabname,
        classCfg: selectRequestClassCfg(root)
    }));
_define_property(L131Split, "actionMap", {
    persistSplitter,
    go: Actions.go
});
// L131Split is not available in ssr
L131Split = isSSR ? L131 : connect('L131Split', L131Split);
export { Blank, L1, L1Col, L131, Responsive, L131Split };
